import { useCustom, useCustomMutation, useGo } from '@refinedev/core';
import { useMemo } from 'react';
import { notify } from '@/lib/notify';
import { Plus, Eye, Pencil, Trash2, Lock, Flag } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DataTable,
  TableToolbar,
  FilterSelect,
  type Column,
  type BulkAction,
  type FilterOption,
} from '@/components/tables';
import { FormHeader } from '@/components/forms';
import { useConfirm } from '@/hooks/use-confirm';
import { usePersistedState } from '@/hooks/use-persisted-state';
import { useScrollRestoration } from '@/hooks/use-scroll-restoration';
import type { OrderStatusConfig } from './types';

type StatusTypeFilter = 'all' | 'system' | 'custom' | 'initial' | 'final';

const statusTypeOptions: FilterOption[] = [
  { label: 'Системные', value: 'system' },
  { label: 'Пользовательские', value: 'custom' },
  { label: 'Начальные', value: 'initial' },
  { label: 'Финальные', value: 'final' },
];

export function OrderStatusesList() {
  useScrollRestoration();
  const go = useGo();
  const [search, setSearch] = usePersistedState('order-statuses:search', '');
  const [typeFilter, setTypeFilter] = usePersistedState<StatusTypeFilter>('order-statuses:typeFilter', 'all');
  const [page, setPage] = usePersistedState('order-statuses:page', 1);
  const pageSize = 20;

  const statusesQuery = useCustom<OrderStatusConfig[]>({
    url: '/order-statuses',
    method: 'get',
  });

  const queryData = statusesQuery.query?.data?.data;
  const isLoading = statusesQuery.query?.isLoading ?? false;

  // Client-side search and filter (backend returns flat list without query support)
  const filteredData = useMemo(() => {
    const rawData = queryData ?? [];
    let result = [...rawData].sort((a, b) => a.position - b.position);

    if (search) {
      const lower = search.toLowerCase();
      result = result.filter(
        (s) =>
          s.name.toLowerCase().includes(lower) ||
          (s.description?.toLowerCase().includes(lower) ?? false),
      );
    }

    if (typeFilter === 'system') {
      result = result.filter((s) => s.isSystem);
    } else if (typeFilter === 'custom') {
      result = result.filter((s) => !s.isSystem);
    } else if (typeFilter === 'initial') {
      result = result.filter((s) => s.isInitial);
    } else if (typeFilter === 'final') {
      result = result.filter((s) => s.isFinal);
    }

    return result;
  }, [queryData, search, typeFilter]);

  // Client-side pagination
  const total = filteredData.length;
  const pageData = filteredData.slice((page - 1) * pageSize, page * pageSize);

  const deleteMutation = useCustomMutation();
  const { confirm, ConfirmationDialog } = useConfirm();

  const handleDelete = async (items: OrderStatusConfig[]) => {
    if (items.length === 0) { return; }

    const deletable = items.filter((s) => !s.isSystem);
    if (deletable.length === 0) {
      notify.error('Нельзя удалить системные статусы');
      return;
    }
    if (deletable.length < items.length) {
      notify.warning(
        `Будут удалены ${deletable.length} из ${items.length} (системные пропущены)`,
      );
    }

    const confirmed = await confirm({
      title:
        deletable.length === 1
          ? `Удалить статус «${deletable[0].name}»?`
          : `Удалить ${deletable.length} статусов?`,
      description: 'Это действие нельзя отменить.',
      variant: 'destructive',
      confirmText: 'Удалить',
      cancelText: 'Отмена',
    });

    if (!confirmed) { return; }

    const results = await Promise.allSettled(
      deletable.map(
        (item) =>
          new Promise<void>((resolve, reject) => {
            deleteMutation.mutate(
              {
                url: `/order-statuses/${item.id}`,
                method: 'delete',
                values: {},
              },
              {
                onSuccess: () => resolve(),
                onError: (error) => reject(error),
              },
            );
          }),
      ),
    );

    const succeeded = results.filter((r) => r.status === 'fulfilled').length;
    const failed = results.length - succeeded;

    if (succeeded > 0) {
      notify.success(succeeded === 1 ? 'Статус удалён' : `Удалено статусов: ${succeeded}`);
      void statusesQuery.query?.refetch();
    }
    if (failed > 0) {
      const firstError = results.find((r): r is PromiseRejectedResult => r.status === 'rejected');
      const errMessage =
        firstError?.reason instanceof Error
          ? firstError.reason.message
          : 'Не удалось удалить часть статусов';
      notify.error(`Ошибок: ${failed}. ${errMessage}`);
    }
  };

  const columns: Column<OrderStatusConfig>[] = [
    {
      key: 'color',
      header: '',
      width: '40px',
      render: (_value, record) => (
        <div
          className="w-4 h-4 rounded-full flex-shrink-0"
          style={{ backgroundColor: record.color }}
        />
      ),
    },
    {
      key: 'name',
      header: 'Название',
      render: (_value, record) => (
        <div>
          <div className="font-medium">{record.name}</div>
          {record.description && (
            <div className="text-xs text-muted-foreground">{record.description}</div>
          )}
        </div>
      ),
    },
    {
      key: 'position',
      header: 'Позиция',
      width: '80px',
      render: (value) => <span className="text-sm text-muted-foreground">{String(value)}</span>,
    },
    {
      key: 'isSystem',
      header: 'Тип',
      width: '240px',
      render: (_value, record) => (
        <div className="flex items-center gap-1 flex-wrap">
          {record.isSystem && (
            <Badge variant="secondary" className="text-xs gap-1">
              <Lock className="h-3 w-3" />
              Системный
            </Badge>
          )}
          {record.isInitial && (
            <Badge variant="outline" className="text-xs gap-1">
              <Flag className="h-3 w-3" />
              Начальный
            </Badge>
          )}
          {record.isFinal && (
            <Badge variant="outline" className="text-xs text-red-500 border-red-300">
              Финальный
            </Badge>
          )}
          {!record.isSystem && !record.isInitial && !record.isFinal && (
            <Badge variant="outline" className="text-xs">Обычный</Badge>
          )}
        </div>
      ),
    },
    {
      key: 'onEnterActions',
      header: 'Действия',
      width: '140px',
      render: (_value, record) => {
        const total = record.onEnterActions.length + record.onExitActions.length;
        if (total === 0) {
          return <span className="text-muted-foreground text-sm">—</span>;
        }
        return (
          <Badge variant="outline" className="text-xs">
            {total} действий
          </Badge>
        );
      },
    },
  ];

  const bulkActions: BulkAction<OrderStatusConfig>[] = [
    {
      label: 'Просмотр',
      icon: <Eye className="h-4 w-4" />,
      onClick: (items) => {
        if (items.length === 1) {
          go({ to: `/orders/statuses/show/${items[0].id}` });
        }
      },
      showOnlyWhenSelected: true,
    },
    {
      label: 'Редактировать',
      icon: <Pencil className="h-4 w-4" />,
      onClick: (items) => {
        if (items.length === 1) {
          go({ to: `/orders/statuses/edit/${items[0].id}` });
        }
      },
      showOnlyWhenSelected: true,
    },
    {
      label: 'Удалить',
      icon: <Trash2 className="h-4 w-4" />,
      variant: 'destructive',
      onClick: (items) => { void handleDelete(items); },
      showOnlyWhenSelected: true,
    },
  ];

  return (
    <div className="space-y-6">
      <FormHeader
        title="Статусы заказов"
        description="Управление статусами и переходами"
        sticky
        actions={
          <Button onClick={() => go({ to: '/orders/statuses/create' })}>
            <Plus className="h-4 w-4 mr-2" />
            Добавить статус
          </Button>
        }
      />

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[240px]">
          <TableToolbar
            searchValue={search}
            onSearchChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            searchPlaceholder="Поиск по названию или описанию..."
          />
        </div>
        <FilterSelect
          label="Тип статуса"
          value={typeFilter}
          options={statusTypeOptions}
          allLabel="Все типы"
          onChange={(value) => {
            setTypeFilter(value as StatusTypeFilter);
            setPage(1);
          }}
          className="w-[180px]"
        />
      </div>

      <DataTable
        columns={columns}
        data={pageData}
        total={total}
        page={page}
        pageSize={pageSize}
        isLoading={isLoading}
        onPageChange={setPage}
        onRowClick={(record) => go({ to: `/orders/statuses/edit/${record.id}` })}
        emptyMessage="Нет статусов"
        selectable
        rowKey="id"
        bulkActions={bulkActions}
      />

      {ConfirmationDialog}
    </div>
  );
}
